import { ApolloClient, from, InMemoryCache, split } from '@apollo/client'
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries'
import { getMainDefinition } from '@apollo/client/utilities'
import { sha256 } from 'crypto-hash'

import generateIntrospection from '@/network/graphql/introspection-result.generated.json'

import { fixedRelayStylePagination } from './helper'
import { authLink } from './link/auth.link'
import { dateServerLink } from './link/date.link'
import { getErrorLink } from './link/error.link'
import { logTimeLink } from './link/logTime.link'
import { recaptchaLink } from './link/recaptcha.link'
import { refreshAuthLink } from './link/refreshAuth.link'
import { retryLink } from './link/retry.link'
import { timeoutHttpLink } from './link/timeout.link'
import { timeStartLink } from './link/timeStart.link'
import { getWebsocketLink } from './link/websocket.link'

import type { NormalizedCacheObject } from '@apollo/client'

type InitApolloClientParams = (params: {
  raiseNetworkError: () => void
  raiseInternalError: () => void
  setNextTimeBeforeRetry: (time: number) => void
}) => ApolloClient<NormalizedCacheObject>

export const initApolloClient: InitApolloClientParams = ({
  raiseNetworkError,
  raiseInternalError,
  setNextTimeBeforeRetry,
}) => {
  const wsLink = getWebsocketLink(setNextTimeBeforeRetry)
  const persistedQueryLink = createPersistedQueryLink({ sha256 })
  const errorLink = getErrorLink({ raiseInternalError, raiseNetworkError })

  const splitLink = persistedQueryLink.concat(
    split(
      ({ query }) => {
        const definition = getMainDefinition(query)
        return definition.kind === 'OperationDefinition' && definition.operation === 'subscription'
      },
      wsLink,
      from([refreshAuthLink, recaptchaLink, authLink, dateServerLink, timeStartLink, logTimeLink, timeoutHttpLink])
    )
  )

  return new ApolloClient({
    link: from([errorLink, retryLink, splitLink]),
    cache: new InMemoryCache({
      possibleTypes: generateIntrospection.possibleTypes,
      typePolicies: {
        Show: {
          fields: {
            customers: fixedRelayStylePagination(),
            orderedProducts: fixedRelayStylePagination(),
            presentUsers: fixedRelayStylePagination(),
            ShowFeedItemConnection: fixedRelayStylePagination(),
          },
        },
        User: {
          fields: {
            pastAndCurrentShowsByStartDateDesc: fixedRelayStylePagination(),
            sellerConfig: { merge: true },
            allModerators: fixedRelayStylePagination(),
          },
        },
        Query: {
          fields: {
            getSellerShipmentsByStatus: fixedRelayStylePagination(['statuses']),
            getSellerCancelledAndRefundedOrders: fixedRelayStylePagination(),
            customersWithUnshippedOrderedProducts: fixedRelayStylePagination(['sort']),
          },
        },
        OrderedProduct: {
          fields: {
            order: {
              merge: true,
            },
          },
        },
      },
    }),
    defaultOptions: {
      watchQuery: {
        // TODO: shouldn't this rather be 'no-cache'?
        // Most of data fetched in the studio are updated frequently
        // outside of the Studio. To make sure displayed data are up
        // to date, 'cache-and-network' seemed like a better option.
        // See https://medium.com/@galen.corey/understanding-apollo-fetch-policies-705b5ad71980
        fetchPolicy: 'cache-and-network',
      },
    },
  })
}
